import { useEffect, useRef, useState } from 'react';
import { SiteLink } from './RedesignShell';
import './project-tools.css';

const channels = [
  {
    title: 'Интернет-магазин',
    tag: 'SHOP · Я.ДИРЕКТ · SEO',
    text: 'Привлечение нового трафика через Яндекс.Директ и органический поиск — точка входа в воронку.',
    action: 'Клиент находит товар в магазине и оформляет заказ.',
  },
  {
    title: 'Мобильное приложение',
    tag: 'IOS · ANDROID · PUSH · LOYALTY',
    text: 'Рост LTV через программу лояльности и регулярные касания — превращаем покупателя в постоянного.',
    action: 'Клиент возвращается в приложение и оформляет покупку.',
  },
  {
    title: 'Мини-приложения в мессенджерах',
    tag: 'VK · MAX · TELEGRAM · SMM',
    text: 'Дополнительный источник заказов: реклама в мессенджерах, SMM и LTV через push и сообщения.',
    action: 'Клиент выбирает товар в мини-приложении мессенджера.',
  },
  {
    title: 'Админ-панель и CRM',
    tag: 'CRM · РЕТАРГЕТ · АНАЛИТИКА',
    text: 'Ретаргетинг и сквозная аналитика по всем каналам — всё собрано в одном месте.',
    action: 'Менеджер видит заказы из всех каналов в одном рабочем пространстве.',
  },
];
const phases = [
  ['Новый заказ', 'Товар, контакт клиента и источник заказа поступают в общий контур.'],
  [
    'CRM и обработка',
    'Заказ появляется в CRM. Менеджер видит клиента, состав покупки и её статус.',
  ],
  [
    'Уведомление клиента',
    'Изменение статуса связывает работу команды и канал, в котором клиент сделал заказ.',
  ],
  [
    'Повторная покупка',
    'История заказов помогает продолжить общение и вернуть клиента в магазин или приложение.',
  ],
];
const incomingPaths = [
  'M85 70H175Q195 70 195 90V180Q195 200 215 200H320',
  'M85 200H320',
  'M85 330H175Q195 330 195 310V220Q195 200 215 200H320',
];

function ChannelIcon({ index }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      {index === 0 && (
        <>
          <path d="M8 19h32l-4-9H12l-4 9Zm3 0v21h26V19M19 40V28h10v12M8 19c0 8 8 8 8 0 0 8 8 8 8 0 0 8 8 8 8 0 0 8 8 8 8 0" />
        </>
      )}
      {index === 1 && (
        <>
          <rect x="12" y="4" width="24" height="40" rx="3" />
          <path d="M20 9h8M20 38h8M18 20l4 4 8-8" />
        </>
      )}
      {index === 2 && (
        <>
          <path d="M7 9h34v25H23L13 42v-8H7V9Z" />
          <path d="m16 20 6 5 12-10M16 15h10" />
        </>
      )}
      {index === 3 && (
        <>
          <rect x="5" y="8" width="38" height="31" rx="2" />
          <path d="M5 17h38M17 17v22M23 24h13M23 31h9" />
        </>
      )}
    </svg>
  );
}

export default function CommerceEcosystem() {
  const [active, setActive] = useState(0);
  const [phase, setPhase] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [reduced, setReduced] = useState(false);
  const region = useRef(null);
  const userPaused = useRef(false);
  const [visible, setVisible] = useState(typeof IntersectionObserver === 'undefined');
  const [documentVisible, setDocumentVisible] = useState(true);
  const running = playing && visible && documentVisible && !reduced;
  const source = active < 3 ? active : 0;
  const signalPath =
    phase < 2
      ? incomingPaths[source]
      : phase === 2
        ? 'M320 200H525'
        : 'M525 200V390H40V' + [70, 200, 330][source] + 'H85';
  useEffect(() => {
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)') || {
      matches: false,
      addEventListener() {},
      removeEventListener() {},
    };
    const update = () => {
      setReduced(media.matches);
      if (media.matches) setPlaying(false);
    };
    update();
    media.addEventListener('change', update);
    if (typeof IntersectionObserver === 'undefined')
      return () => media.removeEventListener('change', update);
    // Цикл идёт только в видимой вкладке и области экрана; пауза пользователя сохраняется.
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          if (!media.matches && !userPaused.current) setPlaying(true);
        } else {
          setVisible(false);
        }
      },
      { threshold: 0.12 },
    );
    observer.observe(region.current);
    const visibility = () => setDocumentVisible(!document.hidden);
    document.addEventListener('visibilitychange', visibility);
    visibility();
    return () => {
      document.removeEventListener('visibilitychange', visibility);
      observer.disconnect();
      media.removeEventListener('change', update);
    };
  }, []);
  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setTimeout(() => {
      setPhase((value) => (value + 1) % phases.length);
      if (phase === phases.length - 1) setActive((value) => (value >= 2 ? 0 : value + 1));
    }, 1900);
    return () => window.clearTimeout(timer);
  }, [running, phase]);
  const selectChannel = (index) => {
    setActive(index);
    userPaused.current = false;
    setPlaying(!reduced);
    setPhase(index === 3 ? 1 : 0);
  };
  const showPhase = (index) => {
    userPaused.current = true;
    setPlaying(false);
    setPhase(index);
  };
  const replay = () => {
    userPaused.current = false;
    setPhase(0);
    setPlaying(!reduced);
  };
  return (
    <section
      className="ink commerce-ecosystem sec-pad"
      id="ecommerce-ecosystem"
      ref={region}
      aria-labelledby="ecosystem-title"
    >
      <div className="container">
        <div className="ecosystem-heading">
          <div>
            <span className="eyebrow cyan">— экосистема для интернет-магазина</span>
            <p id="ecosystem-title" className="ecosystem-title">
              Делаем не сайт —<br />
              собираем <span>экосистему</span> продаж.
            </p>
          </div>
          <p>
            Магазин, мобильное приложение, мини-приложения в мессенджерах и собственная CRM —{' '}
            <strong>в одном контуре.</strong> Каждый канал работает на следующий: трафик из
            Я.Директа становится подпиской в Telegram, подписка — постоянным клиентом, клиент —
            данными для ретаргетинга.
          </p>
        </div>
        <p className="ecosystem-instruction">
          Выберите канал и посмотрите, как заказ проходит через систему.
        </p>
        <div className="ecosystem-channels">
          {channels.map((channel, index) => (
            <button
              key={channel.title}
              className={'ecosystem-channel' + (active === index ? ' is-active' : '')}
              aria-pressed={active === index}
              onClick={() => selectChannel(index)}
              aria-controls="ecosystem-demo"
            >
              <span className="ecosystem-channel-top">
                <span>{'0' + (index + 1)}</span>
                <ChannelIcon index={index} />
              </span>
              <strong>{channel.title}</strong>
              <span className="ecosystem-channel-copy">{channel.text}</span>
              <small>{channel.tag}</small>
            </button>
          ))}
        </div>
        <div
          className={'ecosystem-demo phase-' + phase + (running ? ' is-playing' : '')}
          id="ecosystem-demo"
        >
          <div
            className="ecosystem-diagram"
            aria-label="Интернет-магазин, мобильное приложение и mini apps связаны с CRM и клиентом"
          >
            <svg
              className="ecosystem-wires"
              viewBox="0 0 600 400"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <path d={incomingPaths.join(' ') + ' M320 200H525 M525 200V390H40V70H85'} />
              <path className="ecosystem-live-wire" d={signalPath} />
              {running && (
                <circle
                  key={phase}
                  r="4"
                  fill="currentColor"
                  stroke="none"
                  className="ecosystem-order-signal"
                >
                  <animateMotion dur="1.9s" path={signalPath} fill="freeze" />
                </circle>
              )}
            </svg>
            {channels.slice(0, 3).map((channel, index) => (
              <button
                type="button"
                key={channel.title}
                className={
                  'eco-node eco-source eco-source-' + index + (active === index ? ' lit' : '')
                }
                aria-label={'Канал: ' + channel.title}
                aria-pressed={active === index}
                onClick={() => selectChannel(index)}
              >
                <ChannelIcon index={index} />
                <span>{['Интернет-магазин', 'Приложение', 'Mini Apps'][index]}</span>
              </button>
            ))}
            <div className={'eco-node eco-crm' + (phase === 1 ? ' lit' : '')}>
              <span className="eco-brand">F/</span>
              <span>CRM</span>
            </div>
            <div className={'eco-node eco-client' + (phase === 2 ? ' lit' : '')}>
              <svg
                viewBox="0 0 48 48"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                aria-hidden="true"
              >
                <circle cx="24" cy="15" r="7" />
                <path d="M10 40v-6a14 14 0 0 1 28 0v6" />
              </svg>
              <span>Клиент</span>
            </div>
            <span className="eco-wire-label eco-label-top">три канала → одна CRM</span>
            <span className="eco-wire-label eco-label-bottom">история → повторная покупка</span>
          </div>
          <div className="ecosystem-order">
            <div className="ecosystem-order-meta">
              <span>Демонстрация · не реальные данные</span>
              <span className="eco-status-dot" />
            </div>
            <p className="ecosystem-order-title">
              Один заказ.
              <br />
              Вся система в работе.
            </p>
            <div
              className="ecosystem-status"
              aria-live={playing ? 'off' : 'polite'}
              aria-atomic="true"
            >
              <strong>{phases[phase][0]}</strong>
              <p>{phase === 0 ? channels[active].action : phases[phase][1]}</p>
            </div>
            <div className="ecosystem-controls">
              <button
                onClick={
                  playing
                    ? () => {
                        userPaused.current = true;
                        setPlaying(false);
                      }
                    : replay
                }
              >
                {playing
                  ? 'Приостановить'
                  : reduced
                    ? 'Вернуться к началу'
                    : 'Показать путь заказа ↗'}
              </button>
              <button
                aria-label="Следующий этап заказа"
                onClick={() => showPhase((phase + 1) % phases.length)}
              >
                →
              </button>
            </div>
          </div>
        </div>
        <div className="ecosystem-phases" aria-label="Этапы заказа">
          {phases.map(([name], index) => (
            <button key={name} onClick={() => showPhase(index)} aria-pressed={phase === index}>
              <span>{'0' + (index + 1)}</span>
              {name}
            </button>
          ))}
        </div>
        <div className="ecosystem-result">
          <div>
            <span className="ecosystem-result-mark">
              fatal<span>it</span>y
            </span>
            <p>
              Одна система, четыре канала, ноль утечек выручки. Полный контроль над воронкой — от
              первого клика до повторного заказа.
            </p>
          </div>
          <div className="ecosystem-result-actions">
            <SiteLink href="/calculator/?project=ecosystem" className="btn btn-primary">
              Собрать экосистему →
            </SiteLink>
            <SiteLink href="/portfolio/tech-store/" className="btn btn-ghost-dark">
              Смотреть кейс ↗
            </SiteLink>
          </div>
        </div>
      </div>
    </section>
  );
}
